import React, { useEffect, useState, useCallback } from 'react';
import { View, Text, StyleSheet, FlatList, TouchableOpacity, ActivityIndicator, RefreshControl, Platform } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { Image } from 'expo-image';
import { Ionicons, MaterialCommunityIcons } from '@expo/vector-icons';
import { getSavedFeeds, getLists, getConvos, getDid } from '../services/bluesky';
import { Theme, useTheme, useThemedStyles } from '../theme';
import { useTranslation } from 'react-i18next';

const CACHE_KEY_ROOMS = 'cache_chat_rooms';
const CACHE_KEY_TIMESTAMP = 'cache_chat_rooms_timestamp';
const CACHE_EXPIRATION = 5 * 60 * 1000; // 5 minutes

interface ChatRoom {
  id: string;
  title: string;
  subtitle: string;
  avatarUrl?: string;
  type: 'following' | 'feed' | 'list' | 'convo';
  uri?: string;
  otherDid?: string;
  lastMessageTime?: string;
}

interface Props {
  onSelectRoom: (room: ChatRoom) => void;
  onSearch?: () => void;
  onNewMessage?: () => void;
  onOpenFeedSearch?: () => void;
}

const ChatList: React.FC<Props> = ({ onSelectRoom, onSearch, onNewMessage, onOpenFeedSearch }) => {
  const [rooms, setRooms] = useState<ChatRoom[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [convoCursor, setConvoCursor] = useState<string | undefined>(undefined);
  const [loadingMore, setLoadingMore] = useState(false);
  const insets = useSafeAreaInsets();
  const { theme } = useTheme();
  const { t } = useTranslation();
  const styles = useThemedStyles(createStyles);

  const formatTime = (dateStr?: string) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 60) return t('chatList.minutesAgo', { count: mins });
    const hours = Math.floor(mins / 60);
    if (hours < 24) return t('chatList.hoursAgo', { count: hours });
    return t('chatList.date', { month: date.getMonth() + 1, day: date.getDate() });
  };

  const getCacheKeys = useCallback(() => {
    const did = getDid();
    return {
      rooms: `${CACHE_KEY_ROOMS}_${did}`,
      timestamp: `${CACHE_KEY_TIMESTAMP}_${did}`
    };
  }, []);

  const loadCachedRooms = useCallback(async () => {
    try {
      const keys = getCacheKeys();
      const cached = await AsyncStorage.getItem(keys.rooms);
      const timestamp = await AsyncStorage.getItem(keys.timestamp);
      
      if (cached) {
        setRooms(JSON.parse(cached));
        setLoading(false);
        
        // If cache is fresh, we don't need to fetch immediately
        if (timestamp && Date.now() - parseInt(timestamp) < CACHE_EXPIRATION) {
          return true; // Cache is fresh
        }
      }
    } catch (e) {
      console.error('Failed to load cached rooms:', e);
    }
    return false; // Cache is missing or stale
  }, [getCacheKeys]);

  const fetchRooms = useCallback(async (isManualRefresh = false) => {
    if (isManualRefresh) setRefreshing(true);
    try {
      const currentDid = getDid();
      const [feeds, lists, convosResult] = await Promise.all([
        getSavedFeeds(),
        getLists(),
        getConvos(),
      ]);

      setConvoCursor(convosResult.cursor);

      const chatRooms: ChatRoom[] = [
        {
          id: 'following',
          title: t('chatList.followingTitle'),
          subtitle: t('chatList.followingSubtitle'),
          type: 'following',
        },
      ];

      // Add DMs
      if (convosResult && convosResult.convos) {
        convosResult.convos.forEach((c: any) => {
          const otherMember = c.members.find((m: any) => m.did !== currentDid);
          chatRooms.push({
            id: c.id,
            title: otherMember?.displayName || otherMember?.handle || 'Unknown',
            subtitle: c.lastMessage?.text || t('chatList.noMessages'),
            avatarUrl: otherMember?.avatar,
            type: 'convo',
            uri: c.id,
            otherDid: otherMember?.did,
            lastMessageTime: c.lastMessage?.sentAt || c.indexedAt,
          });
        });
      }

      feeds.forEach((f: any) => {
        chatRooms.push({
          id: f.uri,
          title: f.displayName,
          subtitle: f.description || t('chatList.feed'),
          avatarUrl: f.avatar,
          type: 'feed',
          uri: f.uri,
        });
      });

      lists.forEach((l: any) => {
        chatRooms.push({
          id: l.uri,
          title: l.name,
          subtitle: l.description || t('chatList.list'),
          avatarUrl: l.avatar,
          type: 'list',
          uri: l.uri,
        });
      });

      setRooms(chatRooms);
      
      // Save to cache
      const keys = getCacheKeys();
      await AsyncStorage.setItem(keys.rooms, JSON.stringify(chatRooms));
      await AsyncStorage.setItem(keys.timestamp, Date.now().toString());
    } catch (e) {
      console.error('Failed to fetch rooms:', e);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [getCacheKeys]);

  const handleLoadMore = useCallback(async () => {
    if (loadingMore || !convoCursor) return;
    
    setLoadingMore(true);
    try {
      const currentDid = getDid();
      const res = await getConvos(convoCursor);
      
      if (res && res.convos && res.convos.length > 0) {
        const newConvos = res.convos.map((c: any) => {
          const otherMember = c.members.find((m: any) => m.did !== currentDid);
          return {
            id: c.id,
            title: otherMember?.displayName || otherMember?.handle || 'Unknown',
            subtitle: c.lastMessage?.text || t('chatList.noMessages'),
            avatarUrl: otherMember?.avatar,
            type: 'convo',
            uri: c.id,
            otherDid: otherMember?.did,
            lastMessageTime: c.lastMessage?.sentAt || c.indexedAt,
          } as ChatRoom;
        });

        setRooms(prev => {
          // Find where the convo section ends (before feeds/lists)
          // Actually, let's just insert them after the last convo
          const lastConvoIndex = [...prev].reverse().findIndex(r => r.type === 'convo');
          if (lastConvoIndex === -1) return [...prev, ...newConvos]; // Should not happen
          
          const actualLastConvoIndex = prev.length - 1 - lastConvoIndex;
          const updated = [...prev];
          updated.splice(actualLastConvoIndex + 1, 0, ...newConvos);
          return updated;
        });
        
        setConvoCursor(res.cursor);
      } else {
        setConvoCursor(undefined);
      }
    } catch (e) {
      console.error('Failed to load more convos:', e);
    } finally {
      setLoadingMore(false);
    }
  }, [convoCursor, loadingMore, t]);

  useEffect(() => {
    loadCachedRooms().then((isFresh) => {
      if (!isFresh) {
        fetchRooms();
      }
    });
  }, [loadCachedRooms, fetchRooms]);

  const renderItem = ({ item }: { item: ChatRoom }) => (
    <TouchableOpacity style={styles.roomItem} onPress={() => onSelectRoom(item)}>
      <View style={styles.avatarContainer}>
        {item.avatarUrl ? (
          <Image source={{ uri: item.avatarUrl }} style={styles.avatar} />
        ) : (
          <View style={[styles.avatar, styles.placeholderAvatar]}>
            <Ionicons 
              name={item.type === 'convo' ? 'person' : (item.type === 'list' ? 'list' : 'logo-rss')} 
              size={24} 
              color={theme.colors.textMuted} 
            />
          </View>
        )}
        {item.type !== 'following' && (
          <View style={styles.typeBadge}>
            <MaterialCommunityIcons 
              name={
                item.type === 'convo' ? 'message-text' : 
                item.type === 'list' ? 'format-list-bulleted' : 
                'rss'
              } 
              size={10} 
              color={theme.colors.primaryText} 
            />
          </View>
        )}
      </View>
      <View style={styles.roomItemContent}>
        <View style={styles.roomInfo}>
          <View style={styles.roomHeader}>
            <Text style={styles.roomTitle} numberOfLines={1}>{item.title}</Text>
            <Text style={styles.roomTime}>{formatTime(item.lastMessageTime)}</Text>
          </View>
          <Text style={styles.roomSubtitle} numberOfLines={1}>{item.subtitle}</Text>
        </View>
        <Ionicons name="chevron-forward" size={18} color={theme.colors.border} style={{ marginLeft: 8 }} />
      </View>
    </TouchableOpacity>
  );

  return (
    <View style={[styles.container, { paddingTop: insets.top }]}>
      <View style={styles.header}>
        <Text style={styles.headerTitle}>{t('tabs.talk')}</Text>
        <View style={styles.headerIcons}>
          <TouchableOpacity style={styles.headerIcon} onPress={onSearch}>
            <Ionicons name="search-outline" size={24} color={theme.colors.text} />
          </TouchableOpacity>
          <TouchableOpacity style={styles.headerIcon} onPress={onOpenFeedSearch}>
            <Ionicons name="chatbubbles-outline" size={24} color={theme.colors.text} />
          </TouchableOpacity>
        </View>
      </View>
      
      <FlatList
        data={rooms}
        keyExtractor={(item) => item.id}
        renderItem={renderItem}
        contentContainerStyle={styles.listContent}
        refreshControl={
          <RefreshControl refreshing={refreshing} onRefresh={() => fetchRooms(true)} tintColor={theme.colors.primary} />
        }
        onEndReached={handleLoadMore}
        onEndReachedThreshold={0.5}
        ListFooterComponent={
          <View>
            {loadingMore && <ActivityIndicator style={{ padding: 16 }} color={theme.colors.primary} />}
            <View style={[styles.footerSpacer, { height: insets.bottom }]} />
          </View>
        }
      />
    </View>
  );
};

const createStyles = (theme: Theme) => StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: theme.colors.background,
  },
  header: {
    height: 60,
    backgroundColor: theme.colors.card,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    borderBottomWidth: 0.5,
    borderBottomColor: theme.colors.border,
  },
  headerTitle: {
    color: theme.colors.text,
    fontSize: 20,
    fontWeight: 'bold',
  },
  headerIcons: {
    flexDirection: 'row',
  },
  headerIcon: {
    marginLeft: 16,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  listContent: {
    paddingVertical: 8,
    backgroundColor: theme.colors.background,
  },
  footerSpacer: {
    backgroundColor: theme.colors.background,
  },
  roomItem: {
    flexDirection: 'row',
    paddingHorizontal: 16,
    paddingVertical: 12,
    alignItems: 'center',
  },
  avatarContainer: {
    marginRight: 12,
  },
  avatar: {
    width: 50,
    height: 50,
    borderRadius: 25, // LINE style rounded corners
    backgroundColor: theme.colors.cardSecondary,
  },
  typeBadge: {
    position: 'absolute',
    bottom: 0,
    right: 0,
    backgroundColor: theme.colors.primary,
    width: 18,
    height: 18,
    borderRadius: 9,
    justifyContent: 'center',
    alignItems: 'center',
    borderWidth: 2,
    borderColor: theme.colors.card,
  },
  placeholderAvatar: {
    backgroundColor: theme.colors.cardSecondary,
    justifyContent: 'center',
    alignItems: 'center',
  },
  roomInfo: {
    flex: 1,
    paddingBottom: 12,
  },
  roomItemContent: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    borderBottomWidth: 0.5,
    borderBottomColor: theme.colors.border,
    paddingBottom: 12,
  },
  roomHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 4,
  },
  roomTitle: {
    fontSize: 16,
    fontWeight: 'bold',
    color: theme.colors.text,
    flex: 1,
  },
  roomTime: {
    fontSize: 12,
    color: theme.colors.textMuted,
  },
  roomSubtitle: {
    fontSize: 14,
    color: theme.colors.textMuted,
  },
});

export default React.memo(ChatList);
